import { forwardRef } from "react";
import { IMaskInput } from "react-imask";
import { cn } from "../lib/cn";
import { useFieldControlProps } from "./Field";

export interface SortCodeInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "size" | "value" | "onChange"
  > {
  /** Unformatted sort code string in/out, e.g. "040004". */
  value?: string;
  /** Called with the unmasked value on every change. */
  onChange?: (value: string) => void;
  /** Error state — error border + aria-invalid. */
  invalid?: boolean;
}

/** Class string copied verbatim from the Input atom (bare input, size md). */
const inputClasses =
  "w-full rounded-lg border border-border-control bg-surface-default px-3 transition-colors " +
  "outline-none focus-visible:border-border-focused focus-visible:ring-2 focus-visible:ring-border-focused-subtle " +
  "h-10 type-body-lg text-content-primary placeholder:text-content-disabled disabled:cursor-not-allowed disabled:opacity-38";

const invalidClasses =
  "border-surface-error focus-visible:border-surface-error";

/** UK bank sort code input — six digits grouped in pairs. */
export const SortCodeInput = forwardRef<HTMLInputElement, SortCodeInputProps>(
  (
    {
      className,
      value,
      onChange,
      invalid = false,
      id,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      ...props
    },
    ref,
  ) => {
    const fieldControlProps = useFieldControlProps({
      id,
      describedBy: ariaDescribedBy,
      invalid: ariaInvalid || invalid,
      required: ariaRequired,
    });
    const resolvedInvalid = Boolean(fieldControlProps["aria-invalid"]);
    return (
      <IMaskInput
        inputRef={ref}
        data-slot="sort-code-input"
        {...fieldControlProps}
        mask="00-00-00"
        unmask={true}
        value={value}
        onAccept={(accepted: string) => {
          onChange?.(accepted);
        }}
        placeholder="04-00-04"
        className={cn(
          inputClasses,
          resolvedInvalid && invalidClasses,
          className,
        )}
        {...props}
      />
    );
  },
);
SortCodeInput.displayName = "SortCodeInput";
